import React from 'react';
import { Box, Typography, Container, Grid, Button } from '@mui/material';
import { LocalShipping, RvHookup, RequestQuote } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { brand } from '../theme';
import logohdf from '../images/logohdf.jpg';

const quickLinks = [
  { text: 'Home', path: '/' },
  { text: 'Hauling Services', path: '/services' },
  { text: 'Trailer Rentals', path: '/rentals' },
  { text: 'Privacy Policy', path: '/privacy' },
];

const Footer: React.FC = () => {
  return (
    <Box
      component="footer"
      sx={{
        background: brand.ink,
        borderTop: `3px solid ${brand.red}`,
        color: 'white',
        mt: 'auto',
      }}
    >
      {/* CTA strip */}
      <Box sx={{ borderBottom: `1px solid ${brand.line}` }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              py: { xs: 4, md: 5 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Need a haul or a trailer?
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>
                Get a fast, no-hassle quote from HDF Hauling.
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/#rental-quote"
              startIcon={<RequestQuote />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                color: 'white',
                whiteSpace: 'nowrap',
                background: `linear-gradient(45deg, ${brand.redDark}, ${brand.red})`,
                boxShadow: '0 4px 16px rgba(225,20,20,0.4)',
                '&:hover': { background: `linear-gradient(45deg, ${brand.red}, ${brand.redBright})` },
              }}
            >
              Get a Quote
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Link columns */}
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ py: { xs: 5, md: 6 } }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  mr: 1.5,
                  border: `2px solid ${brand.red}`,
                }}
              >
                <img
                  src={logohdf}
                  alt="HDF Hauling Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                HDF HAULING
              </Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 360, lineHeight: 1.7 }}>
              North Carolina certified. Reliable hotshot, equipment, and vehicle hauling — plus
              quality trailer rentals for your next job.
            </Typography>
          </Grid>

          <Grid item xs={6} md={3}>
            <Typography variant="overline" sx={{ color: brand.redBright, mb: 1.5, display: 'block' }}>
              Explore
            </Typography>
            {quickLinks.map((link) => (
              <Box
                key={link.text}
                component={Link}
                to={link.path}
                sx={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  py: 0.6,
                  fontSize: '0.95rem',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: 'white' },
                }}
              >
                {link.text}
              </Box>
            ))}
          </Grid>

          <Grid item xs={6} md={4}>
            <Typography variant="overline" sx={{ color: brand.redBright, mb: 1.5, display: 'block' }}>
              What We Do
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.6, color: 'rgba(255,255,255,0.7)' }}>
              <LocalShipping sx={{ fontSize: 20, color: brand.red }} /> Hotshot &amp; Freight Hauling
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.6, color: 'rgba(255,255,255,0.7)' }}>
              <RvHookup sx={{ fontSize: 20, color: brand.red }} /> Trailer Rentals
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, py: 0.6, color: 'rgba(255,255,255,0.7)' }}>
              <RequestQuote sx={{ fontSize: 20, color: brand.red }} /> Free Quotes
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom bar */}
      <Box sx={{ borderTop: `1px solid ${brand.line}` }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              py: 2.5,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              © 2024 HDF Hauling LLC. All rights reserved.
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Site by CursedWardenLabs LLC
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
